<template>
  <div>
    <app-cells>
      <div class="date-picker-wrapper">
        <VueCtkDateTimePicker
          id="date_picker_balance"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          shortcut="thisMonth"
          @is-hidden="onIsHiddenPicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          noClearButton
        />
        <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
      </div>
    </app-cells>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <div class="info-row">
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ analytics_data.clients_total.toLocaleString() }}</span>
            <span class="info-row__text">Clients</span>
          </div>
        </div>
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ analytics_data.clients_insured.toLocaleString() }}</span>
            <span class="info-row__text">Insured</span>
          </div>
        </div>
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ analytics_data.total_amount.toLocaleString() }} USD</span>
            <span class="info-row__text">Amount</span>
          </div>
        </div>
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ analytics_data.clients_not_insured.toLocaleString() }}</span>
            <span class="info-row__text">Not insured</span>
          </div>
        </div>
      </div>
      <app-cells>
        <app-input
          @input.native="onClientSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by client name"
          name="search"
        />
      </app-cells>
      <div v-for="table_item of doctors_table" :key="table_item.name" class="safari-block">
        <app-cells>
          <h2 class="title">{{ table_item.name }}</h2>
          <div v-if="table_item.today" class="status-title" :style="{ 'backgroundColor': '#77b017' }">Today</div>
        </app-cells>
        <v-client-table
          :data="table_item.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default table-default--maxw"
          :class="{ 'table-default--loading': table_preloader }"
        >
          <template #h__group_name><div class="table-default__left">Group name</div></template>
          <template #h__full_name><div class="table-default__left">Full name</div></template>
          <template #group_name="{ row }"><div class="table-default__left">{{ row.group_name }}</div></template>
          <template #full_name="{ row }">
            <div class="table-default__left table-default__left--photo">
              <template v-if="row.client_gender">
                <img v-if="row.client_gender.code === 'male'" src="@/assets/img/male-icon.svg" alt="Gender">
                <img v-if="row.client_gender.code === 'female'" src="@/assets/img/female-icon.svg" alt="Gender">
              </template>
              <img v-else src="@/assets/img/gender-plug-icon.svg" alt="Gender">
              <button
                @click="onCopyToClipboard(`${row.client_first_name} ${row.client_last_name}`)"
                class="table-default__copy"
              >
                {{ row.client_first_name }} {{ row.client_last_name }}
              </button>
            </div>
          </template>
          <template #client_dob="{ row }">
            <button
              @click="onCopyToClipboard(dateFormat(row.client_dob))"
              class="table-default__copy"
            >
              {{ dateFormat(row.client_dob) }}
            </button>
          </template>
          <template #cover>14 days - 500 km</template>
          <template #price="{ row }">{{ new Intl.NumberFormat('en-US').format(row.flying_doctor_price) }}$</template>
          <template #number="{ row }">{{ row.flying_doctor }}</template>
          <template #group_tour_start="{ row }">{{ dateFormat(row.group_tour_start) }}</template>
        </v-client-table>
      </div>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table_length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getSafariAnalyticsFlyingDoctors, getSafariDoctorsByDates } from '@/http'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { format, parse } from 'date-fns'
import debounce from 'lodash/debounce'

export default {
  name: 'SafariAnalyticsFlyingDoctors',
  components: { VueCtkDateTimePicker },
  data() {
    return {
      is_loaded: false,
      analytics_data: {},
      filter_dates: null,
      date_picker_error: false,
      custom_shortcuts: [
        { key: 'thisMonth', label: 'This month', value: 'month' },
        { key: 'lastMonth', label: 'Last month', value: '-month' },
      ],
      table_preloader: false,
      search: '',
      doctors_table: [],
      table_length: 0,
      table: {
        items: [],
        columns: ['full_name', 'client_dob', 'number', 'cover', 'price', 'group_name', 'group_tour_start'],
        options: {
          headings: {
            'full_name': 'Full name',
            'client_dob': 'Date of birth',
            'number': 'Membership number',
            'cover': 'Cover type',
            'price': 'Price',
            'group_name': 'Group name',
            'group_tour_start': 'Tour start',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  created() {
    setTimeout(() => {
      this.$nextTick(() => {
        Promise.all([
          getSafariAnalyticsFlyingDoctors(this.filter_dates.start, this.filter_dates.end),
          getSafariDoctorsByDates({
            page: this.pagination.page,
            page_size: 40,
            group_by: 'group_tour_start',
            range_type: 'month',
            category: 'archive',
            ordering: '-group_tour_start,id',
            search: this.search,
          })
        ])
          .then(response => {
            this.analytics_data = response[0].data
            this.doctors_table = response[1].data.results.length ? response[1].data.results : [{name: '', items: []}]
            this.pagination.pages = response[1].data.pages
            this.pagination.count = response[1].data.count
            this.table_length = 0
            response[1].data.results.forEach(item => {
              this.table_length += item.items.length
            })

            this.is_loaded = true
          })
      })
    }, 0)
  },
  methods: {
    fetchAnalytics() {
      getSafariAnalyticsFlyingDoctors(this.filter_dates.start, this.filter_dates.end)
        .then(response => {
          this.analytics_data = response.data
          this.is_loaded = true
        })
    },
    onIsHiddenPicker() {
      if (this.filter_dates?.start && this.filter_dates?.end) {
        this.date_picker_error = false
        this.fetchAnalytics()
      } else this.date_picker_error = true
    },
    fetchItems() {
      getSafariDoctorsByDates({
        page: this.pagination.page,
        page_size: 40,
        group_by: 'group_tour_start',
        range_type: 'month',
        category: 'archive',
        ordering: '-group_tour_start,id',
        search: this.search,
      })
        .then(response => {
          this.doctors_table = response.data.results.length ? response.data.results : [{name: '', items: []}]
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.table_length = 0
          response.data.results.forEach(item => {
            this.table_length += item.items.length
          })
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    onCopyToClipboard(string) {
      navigator.clipboard.writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    onClientSearch() {
      this.searchClient(this)
    },
    searchClient: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>

<style lang="sass" scoped>
.safari-block
  margin-bottom: 30px
</style>